import {
  flip,
  offset,
  Placement,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import React, { ReactNode, RefObject, useEffect } from "react";
import { createPortal } from "react-dom";

import { useFloatingTransition } from "../../hooks/useFloatingTransition";
import dimensionTokens from "../../tokens/dimension.value.tokens.json";
import ElevatedBox from "../ElevatedBox";

// Use tokens directly - values are already in px
const POPOVER_WIDTH = parseInt(
  dimensionTokens.component.tooltip["max-width"].$value
); // 250px
const POPOVER_OFFSET_VALUE = parseInt(dimensionTokens.spacing["x-sm"].$value); // 8px

interface PopoverProps {
  /** Content to be displayed in the Popover */
  children: ReactNode;
  /** Ref for the trigger element */
  triggerRef: RefObject<HTMLElement | null>;
  /** Whether the Popover is open */
  open: boolean;
  /** Callback when Popover open state changes */
  onOpenChange: (open: boolean) => void;
  /** Placement of popover relative to reference element */
  placement?: Placement;
  /** Classes to be passed to the `ElevatedBox` itself */
  className?: string;
}

/**
 * Renders an `ElevatedBox` with the content passed into `children` .
 */

const Popover: React.FC<PopoverProps> = ({
  children,
  triggerRef,
  open,
  onOpenChange,
  placement = "top",
  className,
}) => {
  const { refs, floatingStyles, context } = useFloating({
    placement: placement,
    middleware: [
      offset(POPOVER_OFFSET_VALUE),
      flip(), // Flips to opposite side when there's no space
      shift({ padding: 8 }), // Shifts along the axis to stay in view
      size({
        apply({ elements }) {
          Object.assign(elements.floating.style, {
            width: `${POPOVER_WIDTH}px`,
          });
        },
      }),
    ],
    open,
    onOpenChange,
  });

  useEffect(() => {
    refs.setReference(triggerRef.current);
  }, [refs, triggerRef]);

  // Interaction hooks for click behavior
  // -------------------------------------------------

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([click, dismiss]);

  // Transition hooks for fade in/out
  // -------------------------------------------------

  const { isMounted, transitionStyles } = useFloatingTransition(context);

  return (
    <>
      {isMounted &&
        createPortal(
          <div
            style={{ ...floatingStyles, ...transitionStyles }}
            ref={refs.setFloating}
            {...getFloatingProps()}
          >
            <ElevatedBox className={className}>{children}</ElevatedBox>
          </div>,
          document.body
        )}
    </>
  );
};

export default Popover;
